import React from "react";
import { useState, useEffect } from "react";
import { Divider, Image } from "antd";
import { FilePdfOutlined } from '@ant-design/icons';
import BVCustomTooltip from "./BVCustomTooltip/bv-custom-tooltip";
import { formatCurrency } from "../../../../../../../../utils/data-format-enchanters/currency-enchanter";

import { BookedForSalesProduct, CustomerFacility } from "../../../../../../../../api-client";
import { useBookedForSales } from "../../../../../../hooks/useBookedForSales";

import BVCancelModal from "./BVModals/bv-cancel-modal";
import BVConfirmModal from "./BVModals/bv-confirm-modal";
import BVCancelButton from "./BVCustomButtons/bv-cancel-button";
import BVConfirmButton from "./BVCustomButtons/bv-confirm-button";
import BVConfirmedButton from "./BVCustomButtons/bv-confirmed-button";
import BVCancelledButton from "./BVCustomButtons/bv-cancelled-button";
import BVContainerLoader from "../../../../../../../../common/components/BVContainerLoader/bv-container-loader";

import "./bv-customer-product-card.scss";

interface IBVCustomersProductCard {
	bookedForSalesProduct: BookedForSalesProduct;
	facilities?: CustomerFacility[];
	refreshBookedForSales: () => void;
}

const BVCustomersProductCard: React.FC<IBVCustomersProductCard> = (props) => {

	const [cancelVisible, setCancelVisible] = useState(false);
	const [confirmVisible, setConfirmVisible] = useState(false);
	const [timeRemaining, setTimeRemaining] = useState("");

	const { handleExportBookedForSalesOrder, exportBookedForSalesOrderLoading } = useBookedForSales();

	const handleCancel = () => {
		setCancelVisible(false);
	};

	const handleConfirm = () => {
		setConfirmVisible(false);
	};

	useEffect(() => {
		const expirationDate = new Date(
			props.bookedForSalesProduct.expirationDate ?? ""
		);

		expirationDate.setMinutes(
			expirationDate.getMinutes() - new Date().getTimezoneOffset()
		);

		const now = new Date();

		if (expirationDate <= now) {
			setTimeRemaining("TIME OUT");
		} else {
			const distance = expirationDate.getTime() - now.getTime();
			const days = Math.floor(distance / (1000 * 60 * 60 * 24));
			const hours = Math.floor(
				(distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
			);

			const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
			setTimeRemaining(`${days}d ${hours}h ${minutes}m`);
		}
	}, [props.bookedForSalesProduct.expirationDate]);

	const CompletedAtDate = new Date(props.bookedForSalesProduct.sentAt ?? "-").toLocaleString();

	const CustomerName = props.facilities?.find(facility => facility.name)?.name ?? "-";

	return (
		<React.Fragment>
			<div className="product-card">
				<div className="grid-container">
					<div></div>
					<div className="sap-code">
						<span className="light">SapCode </span>
						<span>{props.bookedForSalesProduct.sapCode}</span>
					</div>
					<div></div>
					<div className="img-wrapper">
						<Image
							src={props.bookedForSalesProduct.image?.detailUrl}
							alt={props.bookedForSalesProduct.sapDescription}
							preview={{
								className: "products-detail-mask"
							}}
						/>
					</div>
					<div className="product-details-container">
						<div>
							<div className="spacing">
								<div className="light">Description:</div>
								<span className="description">{props.bookedForSalesProduct.sapDescription}</span>
							</div>
							{props.bookedForSalesProduct.catalogCode &&
								props.bookedForSalesProduct.catalogCode !== "" && (
									<div className="spacing">
										<span className="light">Catalog Code:</span>
										<span>{props.bookedForSalesProduct.catalogCode}</span>
									</div>
								)}
						</div>
						{props.bookedForSalesProduct.heroStatus?.code === "BookedForSale" &&
							props.bookedForSalesProduct.status?.code !== "Received" && (
								<div>
									{props.bookedForSalesProduct.sentAt &&
										props.bookedForSalesProduct.sentAt !== "" && (
											<div className="spacing">
												<span className="light">Completed at:</span>
												<span>{CompletedAtDate}</span>
											</div>
										)}
									{props.bookedForSalesProduct.orderReason &&
										props.bookedForSalesProduct.orderReason !== "" && (
											<div className="spacing">
												<span className="light">Order Reason:</span>
												<span>{props.bookedForSalesProduct.orderReason}</span>
											</div>
										)}
									{props.bookedForSalesProduct.orderSapCode &&
										props.bookedForSalesProduct.orderSapCode !== "" && (
											<div className="spacing">
												<span className="light">Order SapCode:</span>
												<span>{props.bookedForSalesProduct.orderSapCode}</span>
											</div>
										)}
									{props.bookedForSalesProduct.orderStatus &&
										props.bookedForSalesProduct.orderStatus !== "" && (
											<div className="spacing">
												<span className="light">Order Status:</span>
												<span>{props.bookedForSalesProduct.orderStatus}</span>
											</div>
										)}
									<div>
										{props.bookedForSalesProduct.shipTo && (
											<div className="spacing">
												<span className="light">Ship To:</span>
												<span>{props.bookedForSalesProduct.shipTo}</span>
											</div>
										)}
										{props.bookedForSalesProduct.note &&
											props.bookedForSalesProduct.note !== "" && (
												<div className="spacing">
													<span className="light">Note:</span>
													<span>{props.bookedForSalesProduct.note}</span>
												</div>
											)}
									</div>
								</div>
							)}
						{props.bookedForSalesProduct.heroStatus?.code === "Cancelled" &&
							props.bookedForSalesProduct.reasonWhyCancel &&
							props.bookedForSalesProduct.reasonWhyCancel !== "" && (
								<div>
									<div className="spacing">
										<span className="light">Reason:</span>
										<span>{props.bookedForSalesProduct.reasonWhyCancel}</span>
									</div>
									{props.bookedForSalesProduct.note &&
										props.bookedForSalesProduct.note !== "" && (
											<div className="spacing">
												<span className="light">Note:</span>
												<span>{props.bookedForSalesProduct.note}</span>
											</div>
										)}
								</div>
							)}
					</div>
					<div className="prices-section-container">
						<div className="prices-section">
							<div className="prices-container">
								<div className="prices-labels light">
									<div>Net Billing</div>
								</div>
								<div className="prices-wrapper">
									<div>
										{props.bookedForSalesProduct.priceNet?.amount !== 0
											? formatCurrency(
												props.bookedForSalesProduct.priceNet?.amount,
												props.bookedForSalesProduct.priceNet?.currency
											)
											: "-"}
									</div>
									<div className="light">
										{props.bookedForSalesProduct.localPriceNet?.currency !== null &&
											props.bookedForSalesProduct.localPriceNet?.currency !== "EUR"
											? (
												<React.Fragment>
													{formatCurrency(
														props.bookedForSalesProduct.localPriceNet?.amount,
														props.bookedForSalesProduct.localPriceNet?.currency
													)}
												</React.Fragment>
											) : null}
									</div>
								</div>
							</div>
							<div className="prices-container">
								<div className="prices-labels light">
									<div>Retail price w\o tax</div>
								</div>
								<div className="prices-wrapper">
									<div>
										{props.bookedForSalesProduct.priceRetail?.amount !== 0
											? formatCurrency(
												props.bookedForSalesProduct.priceRetail?.amount,
												props.bookedForSalesProduct.priceRetail?.currency
											)
											: "-"}
									</div>
									<div className="light">
										{props.bookedForSalesProduct.localPriceRetail?.currency !== null &&
											props.bookedForSalesProduct.localPriceRetail?.currency !== "EUR"
											? (
												<React.Fragment>
													{formatCurrency(
														props.bookedForSalesProduct.localPriceRetail?.amount,
														props.bookedForSalesProduct.localPriceRetail?.currency
													)}
												</React.Fragment>
											) : null}
									</div>
								</div>
							</div>
						</div>
						<div className="message">
							The prices are for indicative purposes.
						</div>
					</div>
					<div className="download-order">
						{props.bookedForSalesProduct.heroStatus?.code === "BookedForSale" &&
							props.bookedForSalesProduct.status?.code !== "Received" &&
							props.bookedForSalesProduct.type === "W" && (
								<BVCustomTooltip
									title={'Download BFS Order'}
									disabled={exportBookedForSalesOrderLoading}
									onClick={() => {
										handleExportBookedForSalesOrder(
											CustomerName, props.bookedForSalesProduct.id);
									}}
								>
									<FilePdfOutlined
										style={{
											color: "#85715d",
											fontSize: '24px',
											cursor: 'pointer'
										}}
									/>
								</BVCustomTooltip>
							)}
					</div>
					<BVContainerLoader
						loading={exportBookedForSalesOrderLoading}
					/>
					<div>
						{props.bookedForSalesProduct.heroStatus?.code === "BookedForSale" &&
							props.bookedForSalesProduct.status?.code === "Received" && (
								<div className="reminder spacing">
									<span className="light">Time Remaining:</span>
									<div
										style={{
											color: timeRemaining === "TIME OUT"
												? "#8B0000" : "#000000",
											fontWeight: timeRemaining === "TIME OUT"
												? 600 : "normal"
										}}
									>
										{timeRemaining}
									</div>
								</div>
							)}
					</div>
					{props.bookedForSalesProduct.heroStatus?.code === "BookedForSale" &&
						props.bookedForSalesProduct.status?.code !== "Received"
						? (
							<div className="buttons-wrapper completed">
								<BVConfirmedButton />
							</div>
						) : props.bookedForSalesProduct.heroStatus?.code === "Cancelled" ? (
							<div className="buttons-wrapper completed">
								<BVCancelledButton />
							</div>
						) : (
							<React.Fragment>
								<div className="buttons-wrapper">
									<BVCancelButton
										setCancelVisible={setCancelVisible}
										timeRemaining={timeRemaining}
									/>
									<BVConfirmButton
										setConfirmVisible={setConfirmVisible}
										timeRemaining={timeRemaining}
									/>
								</div>
								<BVCancelModal
									title={`${CustomerName} - SapCode ${props.bookedForSalesProduct.sapCode}`}
									visible={cancelVisible}
									onOk={handleCancel}
									onCancel={() => setCancelVisible(false)}
									onAccept={props.refreshBookedForSales}
									bookedForSalesProduct={props.bookedForSalesProduct}
								/>
								<BVConfirmModal
									title={`${CustomerName} - SapCode ${props.bookedForSalesProduct.sapCode}`}
									visible={confirmVisible}
									onOk={handleConfirm}
									onCancel={() => setConfirmVisible(false)}
									onAccept={props.refreshBookedForSales}
									bookedForSalesProduct={props.bookedForSalesProduct}
									facilities={props.facilities}
									orderReasons={[]}
								/>
							</React.Fragment>
						)}
				</div>
			</div>
			<Divider />
		</React.Fragment>
	);
};

export default BVCustomersProductCard;